/* ==========================================================================
   WeDoSkin — Navigation / Mega Menu / Search behaviour (wds-nav)
   Vanilla JS, no dependencies. All hooks are data-attributes (data-wds-*).
   ========================================================================== */
(function () {
  'use strict';
  if (window.__wdsNavInit) return;
  window.__wdsNavInit = true;

  var doc = document;

  function ready(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  ready(function () {
    var header = doc.querySelector('[data-wds-header]');
    if (!header) return;

    var section = header.closest('.shopify-section-header') || header;
    var overlay = doc.querySelector('[data-wds-overlay]');
    var navLinks = Array.prototype.slice.call(header.querySelectorAll('[data-wds-mega-trigger]'));
    var megas = {};
    Array.prototype.forEach.call(header.querySelectorAll('[data-wds-mega]'), function (m) {
      megas[m.getAttribute('data-wds-mega')] = m;
    });

    var openKey = null;
    var closeTimer = null;
    var HOVER_CLOSE_DELAY = 160;

    function setHeaderOffset() {
      var rect = header.getBoundingClientRect();
      var offset = Math.max(0, rect.bottom);
      header.style.setProperty('--wds-header-offset', offset + 'px');
    }
    setHeaderOffset();
    window.addEventListener('resize', debounce(setHeaderOffset, 150));
    window.addEventListener('scroll', debounce(setHeaderOffset, 150), { passive: true });

    /* ---------------- mega menus (desktop) ---------------- */
    function trigFor(key) {
      return navLinks.filter(function (l) { return l.getAttribute('data-wds-mega-trigger') === key; })[0] || null;
    }

    function openMega(key) {
      if (openKey === key) return;
      closeSearch(false);
      if (openKey) closeMega(openKey, true);
      var mega = megas[key], trig = trigFor(key);
      if (!mega) return;
      setHeaderOffset();
      mega.classList.add('is-open');
      if (trig) trig.setAttribute('aria-expanded', 'true');
      if (overlay) overlay.classList.add('is-visible');
      openKey = key;
    }

    function closeMega(key, switching) {
      var mega = megas[key], trig = trigFor(key);
      if (mega) mega.classList.remove('is-open');
      if (trig) trig.setAttribute('aria-expanded', 'false');
      if (!switching && overlay) overlay.classList.remove('is-visible');
      if (openKey === key) openKey = null;
    }

    function closeAllMegas() {
      if (openKey) closeMega(openKey, false);
    }

    function cancelClose() { clearTimeout(closeTimer); }
    function scheduleClose() {
      cancelClose();
      closeTimer = setTimeout(closeAllMegas, HOVER_CLOSE_DELAY);
    }

    var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    navLinks.forEach(function (link) {
      var key = link.getAttribute('data-wds-mega-trigger');
      var li = link.closest('li');

      if (hoverCapable) {
        li.addEventListener('mouseenter', function () { if (searchOpen) return; cancelClose(); openMega(key); });
        li.addEventListener('mouseleave', scheduleClose);
      }
      link.addEventListener('click', function (e) {
        if (!megas[key]) return;
        if (openKey === key && hoverCapable) {
          // Menu already open via hover — treat the click as a real link.
          var href = link.getAttribute('href');
          if (href && href !== '#') return;
          e.preventDefault();
          closeMega(key, false);
          return;
        }
        // No-hover devices (touch): first tap opens, second tap follows the link.
        if (openKey === key) return;
        e.preventDefault();
        cancelClose();
        openMega(key);
      });
      link.addEventListener('focus', function () { cancelClose(); });
    });

    Object.keys(megas).forEach(function (key) {
      var mega = megas[key];
      if (hoverCapable) {
        mega.addEventListener('mouseenter', cancelClose);
        mega.addEventListener('mouseleave', scheduleClose);
      }
      // keep open while focus is inside; close when focus leaves header entirely
      mega.addEventListener('focusout', function () {
        setTimeout(function () {
          // Never close while this mega hosts the open routine modal — the
          // concierge's list re-render detaches the focused checkbox, which
          // reads as focus "leaving" the header.
          if (mega.classList.contains('wds-modal-host')) return;
          if (openKey === key && !mega.contains(doc.activeElement) && !header.contains(doc.activeElement)) {
            closeMega(key, false);
          }
        }, 10);
      });
    });

    if (overlay) overlay.addEventListener('click', function () { closeAllMegas(); closeSearch(true); });

    doc.addEventListener('click', function (e) {
      if (!openKey) return;
      var t = e.target;
      // The concierge re-renders its product list on toggle, so by the time a
      // checkbox click bubbles here the target is DETACHED — that is not an
      // "outside" click. Same for any click inside the routine modal itself.
      if (!t || !doc.contains(t)) return;
      if (t.closest && t.closest('.wdsc-ov')) return;
      if (!header.contains(t)) closeAllMegas();
    });

    doc.addEventListener('keyup', function (e) {
      if (e.key === 'Escape') {
        if (openKey) {
          var trig = trigFor(openKey);
          closeAllMegas();
          if (trig) trig.focus();
        }
        closeSearch(true);
        closeDrawer();
      }
    });

    /* ---------------- brand A–M / N–Z tabs (desktop + drawer) ---------------- */
    Array.prototype.forEach.call(doc.querySelectorAll('[data-wds-tabs]'), function (tabsWrap) {
      var tabs = Array.prototype.slice.call(tabsWrap.querySelectorAll('[data-wds-tab]'));
      var scope = tabsWrap.getAttribute('data-wds-tabs');
      var panels = Array.prototype.slice.call(doc.querySelectorAll('[data-wds-tabpanel][data-wds-tabscope="' + scope + '"]'));

      function activate(name) {
        tabs.forEach(function (t) {
          var on = t.getAttribute('data-wds-tab') === name;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        panels.forEach(function (p) {
          var on = p.getAttribute('data-wds-tabpanel') === name;
          p.classList.toggle('is-active', on);
        });
      }
      tabs.forEach(function (tab) {
        var name = tab.getAttribute('data-wds-tab');
        tab.addEventListener('click', function (e) { e.preventDefault(); activate(name); });
        if (hoverCapable) tab.addEventListener('mouseenter', function () { activate(name); });
        tab.addEventListener('keydown', function (e) {
          var idx = tabs.indexOf(tab), next;
          if (e.key === 'ArrowRight') next = tabs[(idx + 1) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(idx - 1 + tabs.length) % tabs.length];
          if (next) { e.preventDefault(); next.focus(); activate(next.getAttribute('data-wds-tab')); }
        });
      });
    });

    /* ---------------- Build-Your-Routine concern modal (menu pill) ----------------
       The navy "Build Your Routine" pill in the mega + drawer opens a concern-
       selection modal — same routines as the homepage Build Your Routine modal
       (wds-brand-pills), same quick-add-style black backdrop. Markup is injected
       here (#wnrOv on <body>); styles live in wds-nav.css (.wnr-*). Strings are
       localized from the page locale. */
    (function () {
      var ctas = Array.prototype.slice.call(doc.querySelectorAll('[data-wds-routine-cta]'));
      if (!ctas.length) return;
      var isFr = (doc.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
      var T = isFr ? {
        title: 'Créez votre routine', sub: '(Nos meilleures recommandations)', close: 'Fermer',
        badgeOk: 'Bonus routine complète : 10 % de rabais, appliqué automatiquement',
        badgeMin: 'Conservez 3 étapes ou plus pour obtenir 10 % de rabais',
        sub2: 'Sous-total', dsc: 'Rabais routine (10 %)', tot: 'Votre total',
        add: 'Ajouter la routine au panier', adding: 'Ajout...',
        noteOk: 'Rabais routine de 10 % appliqué automatiquement au paiement',
        noteMin: 'Conservez 3 étapes ou plus pour débloquer votre rabais de 10 %',
        loading: 'Création de votre routine…',
        unavailable: 'Ces produits sont momentanément indisponibles — essayez une autre option.',
        err: 'Une erreur s’est produite — veuillez réessayer.',
        rec: '✦ Recommandé',
        tabs: ['Anti-âge', 'Éclat', 'Acné et peau grasse', 'Peau sensible', 'Hydratation']
      } : {
        title: 'Build Your Routine', sub: '(Our top recommendations)', close: 'Close',
        badgeOk: 'Complete routine bonus: 10% off, applied automatically',
        badgeMin: 'Keep 3 or more steps for 10% off your routine',
        sub2: 'Subtotal', dsc: 'Routine discount (10%)', tot: 'Your total',
        add: 'Add routine to cart', adding: 'Adding...',
        noteOk: '10% routine discount applied automatically at checkout',
        noteMin: 'Keep 3+ steps to unlock your 10% routine discount',
        loading: 'Building your routine…',
        unavailable: 'These products are momentarily unavailable — try another option.',
        err: 'Something went wrong — please try again.',
        rec: '✦ Recommended',
        tabs: ['Anti-Aging', 'Brightening', 'Acne & Oily', 'Sensitive', 'Hydration']
      };
      var DCODE = 'ROUTINE10', DMIN = 3;
      /* Same routines/handles as the homepage Build Your Routine modal (wds-brand-pills) */
      var DATA = [
        { ic: '✨', groups: [
          { g: T.rec, steps: [['Cleanse','alumier-purifying-gel-cleanser'],['Treat','zo-skin-health-growth-factor-serum'],['Antioxidant','skinceuticals-c-e-ferulic'],['Protect','colorescience-sunforgettable-face-shield-flex']] },
          { g: 'AlumierMD', steps: [['Cleanse','alumier-purifying-gel-cleanser'],['Renew','alumier-retinol-resurfacing-serum-1-0'],['Moisturize','alumier-hydradew'],['Protect','alumier-sheer-hydration-broad-spectrum-spf40-untinted']] },
          { g: 'ZO Skin Health', steps: [['Cleanse','zo-hydrating-cleanser'],['Treat','zo-skin-health-growth-factor-serum'],['Moisturize','zo-recovery-creme'],['Protect','zo-skin-health-sheer-fluid-broad-spectrum-sunscreen-spf-50']] },
          { g: 'SkinCeuticals', steps: [['Cleanse','skinceuticals-lha-cleanser-gel'],['Antioxidant','skinceuticals-c-e-ferulic'],['Renew','skinceuticals-p-tiox-anti-wrinkle-cream'],['Protect','skinceuticals-physical-fusion-uv-defense-spf-50']] },
          { g: 'G.M. Collin', steps: [['Cleanse','gm-collin-hydramucine-cleansing-milk'],['Treat','gm-collin-bota-peptides-serum'],['Moisturize','gm-collin-bota-peptides-cream'],['Eye','gm-collin-bota-peptide-eye-contour-15-ml']] }
        ] },
        { ic: '☀️', groups: [
          { g: T.rec, steps: [['Cleanse','alumier-purifying-gel-cleanser'],['Treat','skinceuticals-phloretin-cf'],['Brighten','alumier-bright-clear-solution'],['Protect','eltamd-uv-clear-spf46-broad-spectrum']] },
          { g: 'AlumierMD', steps: [['Cleanse','alumier-purifying-gel-cleanser'],['Treat','alumier-everactive-c-e-peptide'],['Brighten','alumier-bright-clear-solution'],['Protect','alumier-sheer-hydration-broad-spectrum-spf40-untinted']] },
          { g: 'ZO Skin Health', steps: [['Cleanse','zo-hydrating-cleanser'],['Brighten','zo-skin-health-retinol-skin-brightener-1-0'],['Moisturize','zo-recovery-creme'],['Protect','zo-skin-health-sheer-fluid-broad-spectrum-sunscreen-spf-50']] },
          { g: 'SkinCeuticals', steps: [['Cleanse','skinceuticals-lha-cleanser-gel'],['Treat','skinceuticals-phloretin-cf'],['Brighten','skinceuticals-discoloration-defense'],['Protect','skinceuticals-physical-fusion-uv-defense-spf-50']] },
          { g: 'G.M. Collin', steps: [['Cleanse','gm-collin-hydramucine-cleansing-milk'],['Treat','gm-collin-dark-spot-serum'],['Renew','gm-collin-retinol-advanced-night-cream'],['Protect','gm-collin-spf-45-tinted-sunscreen']] }
        ] },
        { ic: '💧', groups: [
          { g: T.rec, steps: [['Cleanse','alumier-acne-clarifying-cleanser'],['Treat','alumier-acne-balancing-serum'],['Purify','gm-collin-pure-mask'],['Protect','eltamd-uv-clear-spf46-broad-spectrum']] },
          { g: 'AlumierMD', steps: [['Cleanse','alumier-acne-clarifying-cleanser'],['Treat','alumier-acne-balancing-serum'],['Moisturize','alumier-hydralight'],['Protect','alumier-sheer-hydration-broad-spectrum-spf40-untinted']] },
          { g: 'G.M. Collin', steps: [['Cleanse','gm-collin-puractive-cleansing-gel'],['Treat','gm-collin-puracne-treating-gel'],['Moisturize','gm-collin-puractive-cream'],['Purify','gm-collin-pure-mask']] },
          { g: 'ZO Skin Health', steps: [['Cleanse','zo-skin-health-exfoliating-cleanser-normal-to-oily-skin'],['Treat','zo-skin-health-complexion-renewal-pads'],['Control','zo-skin-health-oil-control-pads-acne-treatment'],['Protect','zo-skin-health-sheer-fluid-broad-spectrum-sunscreen-spf-50']] }
        ] },
        { ic: '🌿', groups: [
          { g: T.rec, steps: [['Cleanse','gm-collin-hydramucine-cleansing-milk'],['Soothe','gm-collin-sensiderm-cream'],['Moisturize','alumier-hydracalm'],['Protect','colorescience-sunforgettable-face-shield-flex']] },
          { g: 'AlumierMD', steps: [['Cleanse','alumier-gentle-foaming-cleanser'],['Soothe','alumier-hydracalm'],['Moisturize','alumier-hydralight'],['Protect','alumier-sheer-hydration-broad-spectrum-spf40-untinted']] },
          { g: 'G.M. Collin', steps: [['Cleanse','gm-collin-hydramucine-cleansing-milk'],['Soothe','gm-collin-sensiderm-cream'],['Mist','gm-collin-sensiderm-soothing-mist'],['Protect','gm-collin-spf-45-tinted-sunscreen']] }
        ] },
        { ic: '💦', groups: [
          { g: T.rec, steps: [['Cleanse','alumier-purifying-gel-cleanser'],['Treat','skinceuticals-hydrating-b5-gel'],['Moisturize','alumier-hydradew'],['Protect','colorescience-sunforgettable-face-shield-flex']] },
          { g: 'AlumierMD', steps: [['Cleanse','alumier-purifying-gel-cleanser'],['Treat','alumier-ultimate-boost-serum'],['Moisturize','alumier-hydradew'],['Protect','alumier-sheer-hydration-broad-spectrum-spf40-untinted']] },
          { g: 'G.M. Collin', steps: [['Cleanse','gm-collin-hydramucine-cleansing-milk'],['Treat','gm-collin-hyaluronic-filler-serum'],['Moisturize','gm-collin-hyaluronic-filler-cream'],['Eye','gm-collin-hyaluronic-filler-eye-contour']] },
          { g: 'SkinCeuticals', steps: [['Cleanse','skinceuticals-lha-cleanser-gel'],['Treat','skinceuticals-hydrating-b5-gel'],['Moisturize','skinceuticals-triple-lipid-restore-2-4-2'],['Protect','skinceuticals-physical-fusion-uv-defense-spf-50']] }
        ] }
      ];

      function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
      function money(c) { var v = (c / 100).toFixed(2); return isFr ? v.replace('.', ',') + ' $' : '$' + v; }
      function track(name, data) {
        try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, data); } catch (err) {}
        try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: name }, data)); } catch (err) {}
      }

      var ov = doc.createElement('div');
      ov.className = 'wnr-ov';
      ov.id = 'wnrOv';
      ov.innerHTML =
        '<div class="wnr-m">' +
          '<div class="wnr-h"><div class="wnr-t">' + T.title + ' <span>' + T.sub + '</span></div>' +
          '<button type="button" class="wnr-x" id="wnrX" aria-label="' + T.close + '">&#215;</button></div>' +
          '<div class="wnr-tabs" id="wnrTabs"></div>' +
          '<div class="wnr-brands" id="wnrBrands"></div>' +
          '<div class="wnr-badge" id="wnrBadge">' + T.badgeOk + '</div>' +
          '<div class="wnr-list" id="wnrList"></div>' +
          '<div class="wnr-f">' +
            '<div class="wnr-line"><span>' + T.sub2 + '</span><span id="wnrSub">' + money(0) + '</span></div>' +
            '<div class="wnr-line dsc" id="wnrDscRow"><span>' + T.dsc + '</span><span id="wnrDsc">-' + money(0) + '</span></div>' +
            '<div class="wnr-line tot"><span>' + T.tot + '</span><span id="wnrTot">' + money(0) + '</span></div>' +
            '<button type="button" class="wnr-btn" id="wnrAdd">' + T.add + '</button>' +
            '<p class="wnr-note" id="wnrNote">' + T.noteOk + '</p>' +
          '</div>' +
        '</div>';
      doc.body.appendChild(ov);

      var CACHE = {};
      function fetchProduct(h) {
        if (CACHE[h]) return CACHE[h];
        CACHE[h] = fetch('/products/' + h + '.js').then(function (r) { if (!r.ok) throw 0; return r.json(); }).catch(function () { return null; });
        return CACHE[h];
      }
      var cur = 0, grp = 0, steps = [];
      function el(id) { return doc.getElementById(id); }
      function renderTabs() {
        el('wnrTabs').innerHTML = DATA.map(function (c, i) {
          return '<button type="button" class="wnr-tab' + (i === cur ? ' on' : '') + '" data-i="' + i + '">' + c.ic + ' ' + esc(T.tabs[i]) + '</button>';
        }).join('');
      }
      function renderBrands() {
        el('wnrBrands').innerHTML = DATA[cur].groups.map(function (g, i) {
          return '<button type="button" class="wnr-bf' + (i === grp ? ' on' : '') + '" data-i="' + i + '">' + esc(g.g) + '</button>';
        }).join('');
      }
      function renderList() {
        el('wnrList').innerHTML = steps.map(function (s, i) {
          return '<div class="wnr-row' + (s.on ? '' : ' off') + '" data-i="' + i + '">' +
            (s.im ? '<img class="wnr-img" src="' + s.im + '" alt="" loading="lazy">' : '<div class="wnr-img"></div>') +
            '<div class="wnr-info"><div class="wnr-st">' + esc(s.st) + '</div><div class="wnr-n">' + esc(s.n) + '</div></div>' +
            '<div class="wnr-p">' + money(s.c) + '</div><div class="wnr-chk">&#10003;</div></div>';
        }).join('');
        var tot = 0, cnt = 0;
        steps.forEach(function (s) { if (s.on) { tot += s.c; cnt++; } });
        var elig = cnt >= DMIN, dsc = elig ? Math.round(tot * 0.10) : 0;
        el('wnrSub').innerHTML = elig ? '<span class="wnr-strike">' + money(tot) + '</span>' : money(tot);
        el('wnrDscRow').style.display = elig ? 'flex' : 'none';
        el('wnrDsc').textContent = '-' + money(dsc);
        el('wnrTot').textContent = money(tot - dsc);
        el('wnrNote').textContent = elig ? T.noteOk : T.noteMin;
        el('wnrBadge').textContent = elig ? T.badgeOk : T.badgeMin;
        el('wnrAdd').disabled = steps.length === 0 || tot === 0;
      }
      function loadGroup() {
        renderTabs(); renderBrands();
        steps = [];
        el('wnrList').innerHTML = '<div class="wnr-load">' + T.loading + '</div>';
        el('wnrAdd').disabled = true;
        var myCur = cur, myGrp = grp;
        var defs = DATA[cur].groups[grp].steps;
        Promise.all(defs.map(function (d) { return fetchProduct(d[1]); })).then(function (prods) {
          if (myCur !== cur || myGrp !== grp) return;
          steps = [];
          prods.forEach(function (p, i) {
            if (!p || !p.available || !p.variants || !p.variants.length) return;
            var v = null;
            for (var j = 0; j < p.variants.length; j++) { if (p.variants[j].available) { v = p.variants[j]; break; } }
            if (!v) return;
            steps.push({ st: defs[i][0], n: p.title, c: v.price, vid: v.id, im: (p.featured_image ? p.featured_image : ''), on: true });
          });
          if (!steps.length) { el('wnrList').innerHTML = '<div class="wnr-load">' + T.unavailable + '</div>'; return; }
          renderList();
        });
      }
      function openModal() {
        closeAllMegas();
        closeSearch(false);
        closeDrawer();
        cur = 0; grp = 0; loadGroup();
        ov.classList.add('open');
        track('wds_routine_open', { source: 'menu-routine', page: window.location.pathname });
      }
      function closeModal() { ov.classList.remove('open'); }
      ctas.forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); openModal(); });
      });
      el('wnrX').addEventListener('click', closeModal);
      ov.addEventListener('click', function (e) { if (e.target === ov) closeModal(); });
      doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && ov.classList.contains('open')) closeModal(); });
      el('wnrTabs').addEventListener('click', function (e) {
        var b = e.target.closest('.wnr-tab'); if (!b) return;
        cur = parseInt(b.getAttribute('data-i'), 10); grp = 0; loadGroup();
      });
      el('wnrBrands').addEventListener('click', function (e) {
        var b = e.target.closest('.wnr-bf'); if (!b) return;
        grp = parseInt(b.getAttribute('data-i'), 10); loadGroup();
      });
      el('wnrList').addEventListener('click', function (e) {
        var row = e.target.closest('.wnr-row'); if (!row) return;
        var s = steps[parseInt(row.getAttribute('data-i'), 10)];
        if (!s) return; s.on = !s.on; renderList();
      });
      el('wnrAdd').addEventListener('click', function () {
        var sel = steps.filter(function (s) { return s.on; });
        var items = sel.map(function (s) { return { id: s.vid, quantity: 1, properties: { _wds_source: 'menu-routine' } }; });
        if (!items.length) return;
        var totalCents = 0; sel.forEach(function (s) { totalCents += s.c; });
        var abtn = el('wnrAdd');
        abtn.textContent = T.adding; abtn.disabled = true;
        var applyDiscount = sel.length >= DMIN ? fetch('/discount/' + DCODE).catch(function () {}) : Promise.resolve();
        applyDiscount.then(function () {
          return fetch(((window.routes && routes.cart_add_url) ? routes.cart_add_url : '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
          });
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        }).then(function (res) {
          track('wds_routine_add', { source: 'menu-routine', items: items.length, value_cents: totalCents, page: window.location.pathname });
          abtn.textContent = T.add; abtn.disabled = false;
          closeModal();
          var cartDrawer = doc.querySelector('cart-drawer');
          if (cartDrawer && res && res.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(res);
          } else {
            var bubble = doc.getElementById('cart-icon-bubble');
            if (res && res.sections && res.sections['cart-icon-bubble'] && bubble) {
              var parsed = new DOMParser().parseFromString(res.sections['cart-icon-bubble'], 'text/html');
              var inner = parsed.querySelector('.shopify-section');
              if (inner) bubble.innerHTML = inner.innerHTML;
            }
          }
        }).catch(function () {
          abtn.textContent = T.err; abtn.disabled = false;
          setTimeout(function () { abtn.textContent = T.add; }, 2500);
        });
      });
    })();

    /* ---------------- search panel ---------------- */
    var searchPanel = doc.querySelector('[data-wds-search-panel]');
    var searchInput = searchPanel ? searchPanel.querySelector('[data-wds-search-input]') : null;
    var searchResults = searchPanel ? searchPanel.querySelector('[data-wds-search-results]') : null;
    var searchClear = searchPanel ? searchPanel.querySelector('[data-wds-search-clear]') : null;
    var searchStatus = searchPanel ? searchPanel.querySelector('[data-wds-search-status]') : null;
    var searchOpeners = Array.prototype.slice.call(doc.querySelectorAll('[data-wds-search-open]'));
    var searchReturnFocusEl = null;
    var searchOpen = false;
    var searchAbort = null;
    var searchCache = {};
    var sectionId = searchPanel ? (searchPanel.getAttribute('data-wds-search-section') || 'wds-predictive-search') : '';

    function openSearch(opener) {
      if (!searchPanel) return;
      closeAllMegas();
      closeDrawer();
      setHeaderOffset();
      searchReturnFocusEl = opener || doc.activeElement;
      searchPanel.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      searchOpen = true;
      setTimeout(function () { if (searchInput) searchInput.focus(); }, 20);
    }

    function closeSearch(returnFocus) {
      if (!searchPanel || !searchOpen) return;
      searchPanel.classList.remove('is-open');
      if (overlay && !openKey) overlay.classList.remove('is-visible');
      searchOpen = false;
      if (returnFocus && searchReturnFocusEl) searchReturnFocusEl.focus();
    }

    searchOpeners.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (searchOpen) closeSearch(true); else openSearch(btn);
      });
    });
    if (searchPanel) {
      var closeBtn = searchPanel.querySelector('[data-wds-search-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { closeSearch(true); });
    }

    function setStatus(msg) {
      if (searchStatus) searchStatus.textContent = msg || '';
    }

    function renderResults(html) {
      if (!searchResults) return;
      searchResults.innerHTML = html;
      bindAddToCart(searchResults);
    }

    function fetchResults(term) {
      if (!window.routes || !routes.predictive_search_url) return;
      var key = term.toLowerCase();
      if (searchCache[key]) { renderResults(searchCache[key]); setStatus(''); return; }
      if (searchAbort) searchAbort.abort();
      searchAbort = new AbortController();
      setStatus(searchPanel.getAttribute('data-wds-loading-text') || 'Searching…');
      var url = routes.predictive_search_url +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=9&resources[limit_scope]=each' +
        '&section_id=' + sectionId;
      fetch(url, { signal: searchAbort.signal })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (text) {
          var el = new DOMParser().parseFromString(text, 'text/html')
            .querySelector('#shopify-section-' + sectionId);
          var html = el ? el.innerHTML : '';
          searchCache[key] = html;
          renderResults(html);
          setStatus('');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          setStatus(searchPanel.getAttribute('data-wds-error-text') || 'Something went wrong — press Enter to see all results.');
        });
    }

    if (searchInput) {
      var onSearchInput = debounce(function () {
        var term = searchInput.value.trim();
        if (searchClear) searchClear.classList.toggle('is-visible', term.length > 0);
        if (!term) { if (searchResults) searchResults.innerHTML = ''; setStatus(''); return; }
        fetchResults(term);
      }, 300);
      searchInput.addEventListener('input', onSearchInput);
    }
    if (searchClear) {
      searchClear.addEventListener('click', function () {
        searchInput.value = '';
        searchClear.classList.remove('is-visible');
        if (searchResults) searchResults.innerHTML = '';
        setStatus('');
        searchInput.focus();
      });
    }

    /* mobile type-in-place search bar: typing opens the panel as a results-only
       area (its own input bar is hidden on mobile) without stealing focus */
    var msearchInput = doc.querySelector('[data-wds-msearch-input]');
    var msearchClear = doc.querySelector('[data-wds-msearch-clear]');
    if (msearchInput && searchPanel) {
      var msearchForm = msearchInput.closest('form');
      var syncMSearchUI = function () {
        if (msearchForm) msearchForm.classList.toggle('has-term', msearchInput.value.trim().length > 0);
      };
      syncMSearchUI();
      msearchInput.addEventListener('input', syncMSearchUI);
      if (msearchClear) {
        msearchClear.addEventListener('click', function () {
          msearchInput.value = '';
          syncMSearchUI();
          if (searchResults) searchResults.innerHTML = '';
          setStatus('');
          closeSearch(false);
        });
      }
      var onMSearchInput = debounce(function () {
        var term = msearchInput.value.trim();
        if (!term) {
          if (searchResults) searchResults.innerHTML = '';
          setStatus('');
          closeSearch(false);
          return;
        }
        if (!searchOpen) {
          closeAllMegas();
          setHeaderOffset();
          searchPanel.classList.add('is-open');
          if (overlay) overlay.classList.add('is-visible');
          searchOpen = true;
          searchReturnFocusEl = msearchInput;
        }
        fetchResults(term);
      }, 300);
      msearchInput.addEventListener('input', onMSearchInput);
      msearchInput.addEventListener('focus', function () {
        if (msearchInput.value.trim() && !searchOpen) onMSearchInput();
      });
    }

    /* ---------------- quick add to cart (search results) ---------------- */
    function bindAddToCart(scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('[data-wds-atc]'), function (btn) {
        if (btn.__wdsBound) return;
        btn.__wdsBound = true;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var id = btn.getAttribute('data-wds-atc');
          if (!id || btn.disabled) return;
          var label = btn.textContent;
          btn.classList.add('is-loading');
          btn.textContent = btn.getAttribute('data-wds-adding-text') || 'Adding…';
          fetch(((window.routes && routes.cart_add_url) ? routes.cart_add_url : '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: parseInt(id, 10), quantity: 1 }],
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname
            })
          })
            .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
            .then(function (res) {
              btn.classList.remove('is-loading');
              btn.textContent = btn.getAttribute('data-wds-added-text') || 'Added ✓';
              setTimeout(function () { btn.textContent = label; }, 2000);
              var drawer = doc.querySelector('cart-drawer');
              if (drawer && res && res.sections && typeof drawer.renderContents === 'function') {
                closeSearch(false);
                drawer.renderContents(res);
              } else {
                var bubble = doc.getElementById('cart-icon-bubble');
                if (res && res.sections && res.sections['cart-icon-bubble'] && bubble) {
                  var parsed = new DOMParser().parseFromString(res.sections['cart-icon-bubble'], 'text/html');
                  var inner = parsed.querySelector('.shopify-section');
                  if (inner) bubble.innerHTML = inner.innerHTML;
                }
              }
            })
            .catch(function () {
              btn.classList.remove('is-loading');
              btn.textContent = btn.getAttribute('data-wds-error-btn-text') || 'Try again';
              setTimeout(function () { btn.textContent = label; }, 2500);
            });
        });
      });
    }
    if (searchResults) bindAddToCart(searchResults);

    /* ---------------- mobile drawer ---------------- */
    var drawer = doc.querySelector('[data-wds-drawer]');
    var drawerOverlay = doc.querySelector('[data-wds-drawer-overlay]');
    var burger = header.querySelector('[data-wds-burger]');
    var drawerOpenState = false;

    // The theme's sticky header section has will-change: transform, which turns
    // it into the containing block for position: fixed descendants. Move the
    // fixed-position elements to <body> so they span the real viewport.
    [drawer, drawerOverlay, overlay].forEach(function (el) {
      if (el && el.parentElement !== doc.body) doc.body.appendChild(el);
    });

    // NOTE: the Skin Concierge routine modal (.wdsc-ov) is position: fixed and
    // must NOT be moved out of its .wdsc-root (the concierge script re-queries
    // inside its root at run time). Instead, wds-nav.css removes the identity
    // transforms / will-change that would otherwise make header ancestors its
    // containing block and clip it.

    // While the routine modal is open, ghost its hosting mega/drawer and drop
    // the page dim so only the modal + its own rgba(0,0,0,.5) backdrop show —
    // matching the theme's quick-add modal exactly.
    Array.prototype.forEach.call(
      doc.querySelectorAll('[data-wds-mega] .wdsc-ov, .wds-drawer .wdsc-ov'),
      function (ov) {
        var host = ov.closest('.wds-mega, .wds-drawer');
        if (!host) return;
        new MutationObserver(function () {
          var open = ov.classList.contains('open');
          host.classList.toggle('wds-modal-host', open);
          if (open) {
            if (overlay) overlay.classList.remove('is-visible');
            if (drawerOverlay) drawerOverlay.classList.remove('is-visible');
          } else {
            if (overlay && openKey) overlay.classList.add('is-visible');
            if (drawerOverlay && drawerOpenState) drawerOverlay.classList.add('is-visible');
          }
        }).observe(ov, { attributes: true, attributeFilter: ['class'] });
      }
    );

    // In the routine modal, ONLY the checkbox toggles an item on/off. The
    // concierge snippet toggles on any click inside the row (and re-renders
    // the whole list, which reads like a refresh) — intercept in the capture
    // phase so title/image/price clicks never untick an item.
    doc.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var row = e.target.closest('.wdsc-row');
      if (!row) return;
      if (e.target.closest('.wdsc-chk')) return; // checkbox still toggles
      e.stopPropagation();
    }, true);

    // Language selector (static <details> fallback): close when clicking
    // outside it, and on Escape — native <details> otherwise stays open.
    doc.addEventListener('click', function (e) {
      var openLang = doc.querySelectorAll('.wds-lang details[open]');
      if (!openLang.length) return;
      for (var i = 0; i < openLang.length; i++) {
        if (!openLang[i].contains(e.target)) openLang[i].removeAttribute('open');
      }
    });
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openLang = doc.querySelectorAll('.wds-lang details[open]');
      for (var i = 0; i < openLang.length; i++) openLang[i].removeAttribute('open');
    });

    function openDrawer() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      if (drawerOverlay) drawerOverlay.classList.add('is-visible');
      doc.body.classList.add('overflow-hidden');
      drawerOpenState = true;
      if (burger) burger.setAttribute('aria-expanded', 'true');
      var closeBtn = drawer.querySelector('[data-wds-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }
    function closeDrawer() {
      if (!drawer || !drawerOpenState) return;
      drawer.classList.remove('is-open');
      if (drawerOverlay) drawerOverlay.classList.remove('is-visible');
      doc.body.classList.remove('overflow-hidden');
      drawerOpenState = false;
      if (burger) { burger.setAttribute('aria-expanded', 'false'); burger.focus(); }
    }
    if (burger) burger.addEventListener('click', function () { drawerOpenState ? closeDrawer() : openDrawer(); });
    if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);
    if (drawer) {
      var dClose = drawer.querySelector('[data-wds-drawer-close]');
      if (dClose) dClose.addEventListener('click', closeDrawer);
    }

    /* "scroll for more" hint on the drawer's horizontal card rails */
    Array.prototype.forEach.call(doc.querySelectorAll('[data-wds-scrollhint]'), function (hint) {
      var wrap = hint.closest('.wds-acc__cardwrap');
      var cards = wrap ? wrap.querySelector('.wds-acc__cards') : null;
      if (!cards) return;
      function update() {
        var atEnd = cards.scrollLeft + cards.clientWidth >= cards.scrollWidth - 8;
        var noOverflow = cards.scrollWidth <= cards.clientWidth + 8;
        hint.classList.toggle('is-hidden', atEnd || noOverflow);
      }
      hint.__wdsUpdate = update;
      hint.addEventListener('click', function () {
        cards.scrollBy({ left: Math.max(160, cards.clientWidth * 0.7), behavior: 'smooth' });
      });
      cards.addEventListener('scroll', debounce(update, 80), { passive: true });
      update();
    });

    /* drawer accordions — only one open at a time (mobile scroll control) */
    Array.prototype.forEach.call(doc.querySelectorAll('[data-wds-acc-toggle]'), function (toggle) {
      toggle.addEventListener('click', function () {
        var panel = doc.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        if (!isOpen) {
          Array.prototype.forEach.call(doc.querySelectorAll('[data-wds-acc-toggle][aria-expanded="true"]'), function (other) {
            if (other === toggle) return;
            var otherPanel = doc.getElementById(other.getAttribute('aria-controls'));
            other.setAttribute('aria-expanded', 'false');
            if (otherPanel) otherPanel.classList.remove('is-open');
          });
        }
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.classList.toggle('is-open', !isOpen);
        if (!isOpen) {
          Array.prototype.forEach.call(panel.querySelectorAll('[data-wds-scrollhint]'), function (h) {
            if (h.__wdsUpdate) setTimeout(h.__wdsUpdate, 30);
          });
        }
      });
    });
  });
})();

/* ==========================================================================
   WDS Concierge query expander (Aug 29, 2026)
   Widens the Skin Concierge's keyword matching WITHOUT touching
   snippets/wds-skin-concierge-core.liquid. Listens in the CAPTURE phase
   (fires before the concierge's own handlers) and, when a query uses
   goal-language the concierge doesn't recognize ("glass skin", "glow",
   "pores", "look younger"...), appends the matching concern words in
   parentheses so the concierge's existing matcher finds them.
   NOTE: CORE below mirrors the BUCKETS regexes inside
   wds-skin-concierge-core.liquid — keep in sync if those ever change.
   ========================================================================== */
(function () {
  'use strict';
  var CORE = [
    /dark spot|pigment|melasma|sun ?damage|uneven tone|uneven skin ?tone|discolou?r|age spot|brown spot|brighten/,
    /acne|break ?out|pimple|oily|blackhead|whitehead|congest|clog|blemish|zit|shine|shiny/,
    /sensitiv|redness|\bred\b|rosacea|reactive|irritat|flak|itch|burn|sting|post[- ]?procedure|peel|laser|couperose|eczema/,
    /\bneck\b|d[eé]collet|crepe?y|turkey neck|tech ?neck/,
    /uneven texture|\btexture\b|rough|bumpy|grainy|resurfac|\baha\b|glycolic|lactic acid|exfoliat|polish|\bsmooth/,
    /\bdry\b|dryness|dehydrat|tight|parched|hydrat|moistur|dull/,
    /aging|ageing|anti[- ]?age|wrinkle|fine line|firm|sag|mature|collagen|elast|crow|jowl|lax/
  ];
  var MAP = [
    [/glass skin|glazed|dewy|dewiness|glow|radian|lumino|lit from within|honey skin/, 'brighten, smooth texture, hydration'],
    [/\bpores?\b|pore size|minimi[sz]e pores|open pores/, 'oily, uneven texture'],
    [/youthful|younger|\byouth\b|plump|bouncy|snatched|\blift(ed|ing)?\b|prejuvenat|prevent/, 'firm, fine lines'],
    [/even (skin )?tone|even out|brighter|whitening|dark ?marks|acne scars?|post[- ]?acne|hyperpigment/, 'uneven tone, dark spots'],
    [/dark circles?|eye bags?|puffy eyes?|under[- ]?eyes?|tired eyes?/, 'fine lines, firm'],
    [/\bscars?\b|scarring/, 'uneven texture, dark spots'],
    [/barrier|stressed skin|compromis|damaged skin/, 'sensitive, redness, hydration'],
    [/retinol|retinoid|tretinoin|resurface/, 'resurfacing, fine lines'],
    [/vitamin ?c\b|ascorbic/, 'brighten, dark spots'],
    [/niacinamide|vitamin ?b3/, 'oily, brighten'],
    [/hyaluronic|moisture/, 'hydration'],
    [/\bmatte\b|less shiny|greasy|t[- ]?zone/, 'oily'],
    [/\btired\b|lackluster|no glow|flat skin/, 'dull, brighten'],
    [/\bbumps?\b|milia|keratosis|\bkp\b|chicken skin|strawberry skin/, 'uneven texture'],
    [/maskne|hormonal|cystic/, 'acne'],
    [/menopaus|perimenopaus|thin(ning)? skin/, 'mature, firm, hydration'],
    [/\bspf\b|sun ?screen|sun protection/, 'sun damage'],
    [/flaky|peeling skin|rough patches/, 'dry, rough'],
    [/jawline|double chin/, 'firm, sag']
  ];
  function expand(text) {
    var t = text.toLowerCase();
    for (var i = 0; i < CORE.length; i++) { if (CORE[i].test(t)) return null; }
    var adds = [];
    MAP.forEach(function (m) { if (m[0].test(t) && adds.indexOf(m[1]) === -1) adds.push(m[1]); });
    if (!adds.length) return null;
    return text + ' (' + adds.join(', ') + ')';
  }
  function fix(input) {
    if (!input || !input.value) return;
    var next = expand(input.value.trim());
    if (next) input.value = next;
  }
  document.addEventListener('click', function (e) {
    var go = e.target && e.target.closest ? e.target.closest('.wdsc-go') : null;
    if (!go) return;
    var root = go.closest('.wdsc-root');
    if (root) fix(root.querySelector('.wdsc-in'));
  }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var t = e.target;
    if (t && t.classList && t.classList.contains('wdsc-in')) fix(t);
  }, true);
})();
